import { SUBSCRIPTION_STATUS } from '../constants';
import type { User } from '@/types';
import { compareAsc } from 'date-fns';

/**
 * Checks if a user has an active subscription
 * A subscription is considered active if:
 * - Status is ACTIVE (1)
 * - OR Status is PENDING_ASSISTANCE (2)
 * - OR Status is DOWNGRADED (3) - user has scheduled a downgrade
 * - OR Status is CANCELLED (0) but valid_until is in the future
 *
 * This matches the Vue implementation's logic which allows users with
 * cancelled subscriptions that are still valid to access features and
 * change their plan (e.g., when downgrading from yearly to monthly).
 */
export function hasActiveSubscription(user: User | null | undefined): boolean {
    if (!user?.subscriptions?.platform) {
        return false;
    }

    const platformSubscription = user.subscriptions.platform;

    // Check if subscription is currently valid (even if cancelled/downgraded)
    const isStillValid = platformSubscription.valid_until
        ? compareAsc(new Date(platformSubscription.valid_until * 1000), new Date()) >= 0
        : false;

    // Active statuses that allow full access
    const activeStatuses = [
        SUBSCRIPTION_STATUS.ACTIVE,
        SUBSCRIPTION_STATUS.PENDING_ASSISTANCE,
        SUBSCRIPTION_STATUS.DOWNGRADED,
    ];

    // Active if status is in active list OR if cancelled but still valid
    return (
        activeStatuses.includes(platformSubscription.status as (typeof activeStatuses)[number]) ||
        (platformSubscription.status === SUBSCRIPTION_STATUS.CANCELLED && isStillValid)
    );
}
